import { api } from './api'
import type { ConfigurationCodec, ConfigurationMediaRealm, Network, Sip, SipRegisteredDeliverySettingsInfo, SipRegisteredDeliverySettingsUpsertInput, TlsContextInfo } from '@sbclite/core'
import type { SipInterfaceCreateInput, SipInterfaceUpdateInput } from '@sbclite/core/schemas'
import type { Certificate } from '../types/certificate'

// Types matching backend schemas - Migration compatibility type
export type CodecGroup = ConfigurationCodec & {
  // Legacy properties for backward compatibility during migration
  codecId?: string;
  codecName?: string;
  codecType?: 'audio' | 'video' | 'fax';
  payloadType?: number;
  clockRate?: number;
  channels?: number;
  parameters?: Record<string, unknown>;
  // Allow any additional properties during migration
  [key: string]: unknown;
}
export type MediaRealm = ConfigurationMediaRealm
export type TlsContext = TlsContextInfo
export type SipInterface = Network.SipInterfaceInfo

// SIP Peers (Core SST pattern types)
export type SipPeer = Sip.PeerInfo
export type SipManipulation = Sip.ManipulationInfo
export type RegisteredDeliverySettings = SipRegisteredDeliverySettingsInfo

/** The gateway accepts opaque deployment IDs up to its 1024-character field limit. */
export function normalizePushGatewayDeploymentId(value: string): string | null {
  const normalized = value.trim()
  return normalized === '' || normalized.length > 1024 ? null : normalized
}

/** Build the full write payload while keeping server metadata out of API writes. */
export function toRegisteredDeliverySettingsUpsertInput(
  settings: RegisteredDeliverySettings,
  pushGatewayDeploymentId: string,
): SipRegisteredDeliverySettingsUpsertInput {
  return {
    enabled: settings.enabled,
    rolloutPolicy: settings.rolloutPolicy,
    pushGatewayEnabled: settings.pushGatewayEnabled,
    pushGatewayUrl: settings.pushGatewayUrl,
    pushGatewayDeploymentId,
    pushGatewayTimeoutMs: settings.pushGatewayTimeoutMs,
    pushWebhookTokenSecretRef: settings.pushWebhookTokenSecretRef,
    pushWakeHoldMs: settings.pushWakeHoldMs,
    pushActiveFlowFreshMs: settings.pushActiveFlowFreshMs,
    pushDeviceLifetimeSeconds: settings.pushDeviceLifetimeSeconds,
    outboundFlowTokenRequired: settings.outboundFlowTokenRequired,
  }
}

// Enhanced codec configuration with standards integration
export interface CodecConfigurationWithStandards extends ConfigurationCodec {
  standard?: unknown;
  isStandardCompliant?: boolean;
  rfcCompliance?: string[];
}
// Re-exported from the shared leaf module so existing importers keep working.
export type { Certificate } from '../types/certificate'





export interface PeerGroup {
  id: string
  name: string
  description?: string
  enabled: boolean
  groupType: 'teams' | 'carrier' | 'internal' | 'sip-trunk'
  peers: Array<{
    id: string
    name: string
    address: string
    port: number
    transport: 'UDP' | 'TCP' | 'TLS'
    enabled: boolean
    weight?: number
    priority?: number
  }>
  authentication?: {
    enabled: boolean
    method: 'digest' | 'certificate' | 'ip-based'
    credentials?: {
      username?: string
      password?: string
      realm?: string
    }
  }
  loadBalancing: {
    method: 'round-robin' | 'weighted' | 'least-connections'
    healthCheck: boolean
  }
  status: 'active' | 'inactive' | 'maintenance'
  environment?: string
}

export interface RoutingRule {
  routeId: string
  ruleId: string
  name: string
  description?: string
  enabled: boolean
  priority: number
  conditions: {
    sourcePattern?: string
    destinationPattern?: string
    callerIdPattern?: string
    timeOfDay?: {
      start: string
      end: string
      timezone?: string
    }
    dayOfWeek?: string[]
  }
  actions: {
    route: string
    manipulations?: Array<{
      field: 'caller-id' | 'called-number' | 'headers'
      action: 'add' | 'remove' | 'replace' | 'prefix' | 'suffix'
      value: string
      pattern?: string
    }>
    mediaRealm?: string
  }
  statistics?: {
    callCount?: number
    successRate?: number
    lastUsed?: string
  }
  status: 'active' | 'inactive' | 'testing'
  environment?: string
}

export interface DashboardData {
  activeRegistrations: number
  activeCalls: number
  totalCalls: number
  successRate: number
  avgResponseTime: number
  systemHealth: 'healthy' | 'degraded' | 'unhealthy'
}

export interface SystemHealthData {
  containers: Array<{
    containerId: string
    status: 'healthy' | 'degraded' | 'unhealthy'
    cpuUsage?: number
    memoryUsage?: number
    activeCalls: number
    lastHeartbeat: string
    serviceType: string
  }>
  activeCalls: Array<{
    callId: string
    status: string
    duration: number
    containerId: string
  }>
  alerts: Array<{
    severity: 'info' | 'warning' | 'critical'
    message: string
    timestamp: string
  }>
  summary: {
    totalContainers: number
    healthyContainers: number
    degradedContainers: number
    unhealthyContainers: number
    totalActiveCalls: number
  }
}

export interface PerformanceMetrics {
  singleTableBenefits: {
    completeCallData: {
      legacy: { avgLatency: number; requestCount: number }
      singleTable: { avgLatency: number; requestCount: number }
      improvement: { latencyReduction: string; requestReduction: string }
    }
    completeContainerData: {
      legacy: { avgLatency: number; requestCount: number }
      singleTable: { avgLatency: number; requestCount: number }
      improvement: { latencyReduction: string; requestReduction: string }
    }
    systemHealth: {
      legacy: { avgLatency: number; requestCount: number }
      singleTable: { avgLatency: number; requestCount: number }
      improvement: { latencyReduction: string; requestReduction: string }
    }
  }
  timestamp: string
}

export interface SipRegistrationHealth {
  status: 'healthy' | 'degraded' | 'unhealthy'
  activeRegistrations: number
  failedRegistrations: number
  totalRegistrations: number
  successRate: number
}

// API Service Class
export class ConfigurationApiService {
  // Certificates
  async getCertificates(): Promise<Certificate[]> {
    const response = await api.get<{ success: boolean, data: Certificate[], pagination?: unknown }>('/api/certificates')
    return response.data.data || []
  }

  async createCertificate(certificate: Omit<Certificate, 'id'>): Promise<Certificate> {
    const response = await api.post<{ success: boolean, data: Certificate }>('/api/certificates', certificate)
    return response.data.data
  }

  async updateCertificate(id: string, certificate: Partial<Certificate>): Promise<Certificate> {
    const response = await api.put<{ success: boolean, data: Certificate }>(`/api/certificates/${id}`, certificate)
    return response.data.data
  }

  async deleteCertificate(id: string): Promise<void> {
    await api.delete(`/api/certificates/${id}`)
  }

  // Codec Groups
  async getCodecGroups(): Promise<CodecGroup[]> {
    const response = await api.get<{ success: boolean, data: CodecGroup[], pagination?: unknown }>('/api/codec-groups')
    return response.data.data || []
  }

  async createCodecGroup(codecGroup: Partial<CodecGroup>): Promise<CodecGroup> {
    const response = await api.post<{ success: boolean, data: CodecGroup }>('/api/codec-groups', codecGroup)
    return response.data.data
  }

  async updateCodecGroup(id: string, codecGroup: Partial<CodecGroup>): Promise<CodecGroup> {
    const response = await api.put<{ success: boolean, data: CodecGroup }>(`/api/codec-groups/${id}`, codecGroup)
    return response.data.data
  }

  async deleteCodecGroup(id: string): Promise<void> {
    await api.delete(`/api/codec-groups/${id}`)
  }

  // Media Realms
  async getMediaRealms(): Promise<MediaRealm[]> {
    const response = await api.get<{ success: boolean, data: MediaRealm[] }>('/api/media-realms')
    return response.data.data || []
  }

  async createMediaRealm(mediaRealm: Omit<MediaRealm, 'realmId'>): Promise<MediaRealm> {
    const response = await api.post<{ success: boolean, data: MediaRealm }>('/api/media-realms', mediaRealm)
    return response.data.data
  }

  async updateMediaRealm(realmId: string, mediaRealm: Partial<MediaRealm>): Promise<MediaRealm> {
    const response = await api.put<{ success: boolean, data: MediaRealm }>(`/api/media-realms/${realmId}`, mediaRealm)
    return response.data.data
  }

  async deleteMediaRealm(realmId: string): Promise<void> {
    await api.delete(`/api/media-realms/${realmId}`)
  }

  // TLS Contexts
  async getTlsContexts(): Promise<TlsContext[]> {
    const response = await api.get<{ success: boolean; data: TlsContext[] }>('/api/tls-contexts')
    return response.data.data || []
  }

  async createTlsContext(tlsContext: Partial<TlsContext>): Promise<TlsContext> {
    const response = await api.post<{ success: boolean; data: TlsContext }>('/api/tls-contexts', tlsContext)
    return response.data.data
  }

  async updateTlsContext(id: string, tlsContext: Partial<TlsContext>): Promise<TlsContext> {
    const response = await api.put<{ success: boolean; data: TlsContext }>(`/api/tls-contexts/${id}`, tlsContext)
    return response.data.data
  }

  async deleteTlsContext(id: string): Promise<void> {
    await api.delete(`/api/tls-contexts/${id}`)
  }

  // SIP Interfaces
  async getSipInterfaces(): Promise<SipInterface[]> {
    const response = await api.get<{ success: boolean; data: SipInterface[] }>('/api/network/interfaces')
    return response.data.data || []
  }

  async getSipManipulations(): Promise<SipManipulation[]> {
    const response = await api.get<{ success: boolean; data: SipManipulation[] }>('/api/sip-manipulations')
    return response.data.data || []
  }

  async createSipManipulation(manipulation: Partial<SipManipulation>): Promise<SipManipulation> {
    const response = await api.post<{ success: boolean; data: SipManipulation }>('/api/sip-manipulations', manipulation)
    return response.data.data
  }

  async updateSipManipulation(id: string, manipulation: Partial<SipManipulation>): Promise<SipManipulation> {
    const response = await api.put<{ success: boolean; data: SipManipulation }>(`/api/sip-manipulations/${id}`, manipulation)
    return response.data.data
  }

  async createSipInterface(sipInterface: SipInterfaceCreateInput): Promise<SipInterface> {
    const response = await api.post<{ success: boolean; data: SipInterface }>('/api/network/interfaces', sipInterface)
    return response.data.data
  }

  async updateSipInterface(id: string, sipInterface: SipInterfaceUpdateInput): Promise<SipInterface> {
    const response = await api.put<{ success: boolean; data: SipInterface }>(`/api/network/interfaces/${id}`, sipInterface)
    return response.data.data
  }

  async deleteSipInterface(id: string): Promise<void> {
    await api.delete(`/api/network/interfaces/${id}`)
  }

  // Peer Groups
  async getPeerGroups(): Promise<PeerGroup[]> {
    const response = await api.get<{ success: boolean, data: PeerGroup[], pagination?: unknown }>('/api/peer-groups')
    return response.data.data || []
  }

  async createPeerGroup(peerGroup: Omit<PeerGroup, 'id'>): Promise<PeerGroup> {
    const response = await api.post<{ success: boolean, data: PeerGroup }>('/api/peer-groups', peerGroup)
    return response.data.data
  }

  async updatePeerGroup(id: string, peerGroup: Partial<PeerGroup>): Promise<PeerGroup> {
    const response = await api.put<{ success: boolean, data: PeerGroup }>(`/api/peer-groups/${id}`, peerGroup)
    return response.data.data
  }

  async deletePeerGroup(id: string): Promise<void> {
    await api.delete(`/api/peer-groups/${id}`)
  }

  // Registered Delivery Settings
  async getRegisteredDeliverySettings(): Promise<RegisteredDeliverySettings | null> {
    const response = await api.get<{ success: boolean; data: RegisteredDeliverySettings | null }>('/api/registered-delivery-settings/settings')
    return response.data.data || null
  }

  async updateRegisteredDeliverySettings(settings: SipRegisteredDeliverySettingsUpsertInput): Promise<RegisteredDeliverySettings> {
    const response = await api.put<{ success: boolean; data: RegisteredDeliverySettings }>('/api/registered-delivery-settings/settings', settings)
    return response.data.data
  }

  // SIP Peers
  async getSipPeers(params?: {
    type?: 'carrier' | 'teams' | 'webrtc' | 'softphone' | 'pbx'
    enabled?: boolean
    domain?: string
    transport?: 'UDP' | 'TCP' | 'TLS'
  }): Promise<SipPeer[]> {
    const limit = 1000
    let offset = 0
    const all: SipPeer[] = []

    while (true) {
      const response = await api.get<{ success: boolean; data: SipPeer[]; pagination?: unknown }>(
        '/api/sip-peers',
        {
          limit,
          offset,
          ...(params || {})
        }
      )
      const items = response.data.data || []
      all.push(...items)
      if (items.length < limit) break
      offset += limit
    }

    return all
  }

  async createSipPeer(peer: Sip.PeerCreateInput): Promise<SipPeer> {
    const response = await api.post<{ success: boolean; data: SipPeer }>('/api/sip-peers', peer)
    return response.data.data
  }

  async updateSipPeer(peerId: string, peer: Sip.PeerUpdateInput): Promise<SipPeer> {
    const response = await api.put<{ success: boolean; data: SipPeer }>(`/api/sip-peers/${peerId}`, peer)
    return response.data.data
  }

  // SIP Configurations
  // Routing Rules
  async getRoutingRules(): Promise<RoutingRule[]> {
    const response = await api.get<{ success: boolean; data: RoutingRule[] }>('/api/routing-rules')
    return response.data.data || []
  }

  async createRoutingRule(rule: Omit<RoutingRule, 'routeId' | 'ruleId'>): Promise<RoutingRule> {
    const response = await api.post<{ success: boolean; data: RoutingRule }>('/api/routing-rules', rule)
    return response.data.data
  }

  async updateRoutingRule(routeId: string, ruleId: string, rule: Partial<RoutingRule>): Promise<RoutingRule> {
    // Backend uses ruleId as the route parameter, not routeId/ruleId
    const response = await api.put<{ success: boolean; data: RoutingRule }>(`/api/routing-rules/${ruleId}`, rule)
    return response.data.data
  }

  async deleteRoutingRule(routeId: string, ruleId: string): Promise<void> {
    // Backend uses ruleId as the route parameter
    await api.delete(`/api/routing-rules/${ruleId}`)
  }

  // Dashboard Data
  async getDashboardData(): Promise<DashboardData> {
    const response = await api.get<DashboardData>('/api/statistics/dashboard')
    return response.data
  }

  // System Health Data
  // Uses containers endpoint to get system health status
  async getSystemHealth(_timeRange: '1h' | '6h' | '24h' | '7d' = '1h', _includeMetrics: boolean = true): Promise<SystemHealthData> {
    // Get containers from the unified containers endpoint
    const response = await api.get<{
      containers?: Array<{
        containerId: string
        healthStatus?: string
        cpuUsage?: number
        memoryUsage?: number
        currentCalls?: number
        lastHeartbeat?: string
        containerType?: string
      }>
      summary?: { totalContainers?: number; healthyContainers?: number; currentLoad?: number }
    }>('/api/containers?format=detailed')
    const containers = response.data.containers || []

    // Transform containers data to system health format
    return {
      containers: containers.map((c) => ({
        containerId: c.containerId,
        status: c.healthStatus === 'healthy' ? 'healthy' : 'unhealthy',
        cpuUsage: c.cpuUsage || 0,
        memoryUsage: c.memoryUsage || 0,
        activeCalls: c.currentCalls || 0,
        lastHeartbeat: c.lastHeartbeat || '',
        serviceType: c.containerType || ''
      })),
      activeCalls: [],
      alerts: [],
      summary: {
        totalContainers: response.data.summary?.totalContainers || containers.length,
        healthyContainers: response.data.summary?.healthyContainers || containers.filter((c) => c.healthStatus === 'healthy').length,
        degradedContainers: 0,
        unhealthyContainers: containers.filter((c) => c.healthStatus !== 'healthy').length,
        totalActiveCalls: response.data.summary?.currentLoad || 0
      }
    }
  }

  // Performance Metrics
  // Uses statistics endpoint to get performance metrics
  async getPerformanceMetrics(): Promise<PerformanceMetrics> {
    // Return placeholder performance metrics since this is not a critical feature
    return {
      singleTableBenefits: {
        completeCallData: {
          legacy: { avgLatency: 100, requestCount: 3 },
          singleTable: { avgLatency: 15, requestCount: 1 },
          improvement: { latencyReduction: '85%', requestReduction: '67%' }
        },
        completeContainerData: {
          legacy: { avgLatency: 80, requestCount: 2 },
          singleTable: { avgLatency: 12, requestCount: 1 },
          improvement: { latencyReduction: '85%', requestReduction: '50%' }
        },
        systemHealth: {
          legacy: { avgLatency: 150, requestCount: 5 },
          singleTable: { avgLatency: 20, requestCount: 1 },
          improvement: { latencyReduction: '87%', requestReduction: '80%' }
        }
      },
      timestamp: new Date().toISOString()
    }
  }

  // SIP Registration Health
  async getSipRegistrationHealth(): Promise<SipRegistrationHealth> {
    // Use the registrations endpoint to get health status
    const response = await api.get<{ data?: Array<{ registered?: boolean }> }>('/api/sip-registrations')
    const registrations = response.data.data || []
    const activeCount = registrations.filter((r) => r.registered).length
    const failedCount = registrations.filter((r) => !r.registered).length
    
    return {
      status: activeCount > 0 ? 'healthy' : 'degraded',
      activeRegistrations: activeCount,
      failedRegistrations: failedCount,
      totalRegistrations: registrations.length,
      successRate: registrations.length > 0 ? (activeCount / registrations.length) * 100 : 100
    }
  }
}

// Export singleton instance
export const configurationApi = new ConfigurationApiService()
